import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { MoreHoriz } from '@mui/icons-material';
import { Box, Button, Tooltip, IconButton, Tabs, Tab, Typography, useMediaQuery, useTheme } from '@mui/material';
import Avatar from '@ui/components/common/Avatar';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { StyledMenu } from '@ui/components/common/Menu';
import { PageLayout } from '@ui/components/PageLayout';
import { useHelpTopic } from '@ui/contexts';
import { Category, Calendar, AddCircle, Coin } from 'iconsax-react';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import { useEffect, useMemo, useState } from 'react';
import { ScheduleCalendar } from './calendar';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@application/store/useStore';
import { deletePartnerAsync, getAllPartnersAsync, togglePartnerContractAsync } from '@application/features/partner/partnerThunk';
import { PartnerStatusEnum } from '@application/enums/partner-status-enum';
import { APIStatus } from '@application/enums/api-status';
import { getPartnerEventMonthlyAsync } from '@application/features/partnerEvent/partnerEventThunk';
import { hasPermission } from '@application/helpers/permission-helper';
import { toast } from 'react-toastify';
import ResponsiveSearch from '@ui/components/common/ResponsiveSearch';
const Sponsorships = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { partners, status } = useSelector((state) => state.partner);
    const { companies, selectedCompanyId } = useSelector((state) => state.company);
    const { MonthlyPartnerEvent } = useSelector((state) => state.partnerEvent);
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const canReadPartners = hasPermission(loggedInUserPermissions, 'Permissions.Partner.Read');
    const canCreatePartners = hasPermission(loggedInUserPermissions, 'Permissions.Partner.Create');
    const canUpdatePartners = hasPermission(loggedInUserPermissions, 'Permissions.Partner.Update');
    const canDeletePartners = hasPermission(loggedInUserPermissions, 'Permissions.Partner.Delete');
    const [activeTab, setActiveTab] = useState(0);
    const [activeSubTab, setActiveSubTab] = useState(0);
    useHelpTopic(activeSubTab === 0 ? 'partners' : 'calendar');
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [deleteTargetName, setDeleteTargetName] = useState('');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [currentView, setCurrentView] = useState('Month');
    const [lastYearFetched, setLastYearFetched] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    useEffect(() => {
        // const companyIds =
        //   companies && companies.length > 0
        //     ? companies.map((c) => c.id)
        //     : selectedCompanyId
        //       ? [selectedCompanyId]
        //       : [];
        const companyIds = selectedCompanyId ? selectedCompanyId : [];
        if (companyIds.length > 0) {
            void dispatch(getAllPartnersAsync({
                companyIds,
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: searchTerm
            }));
        }
    }, [
        dispatch,
        companies,
        selectedCompanyId,
        paginationModel.page,
        paginationModel.pageSize,
        searchTerm
    ]);
    // Handle Year view - fetch all events for the year
    useEffect(() => {
        if (selectedCompanyId && currentView === 'Year') {
            const currentYear = currentDate.getFullYear();
            if (lastYearFetched !== currentYear) {
                dispatch(getPartnerEventMonthlyAsync({
                    companyId: selectedCompanyId,
                    month: 0,
                    year: currentYear
                }));
                setLastYearFetched(currentYear);
            }
        }
    }, [dispatch, selectedCompanyId, currentView, currentDate.getFullYear(), lastYearFetched]);
    // Handle Month/Week/Day views - make month-specific API calls
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0 && currentView !== 'Year') {
            dispatch(getPartnerEventMonthlyAsync({
                companyId: selectedCompanyId,
                month: currentDate.getMonth() + 1,
                year: currentDate.getFullYear()
            }));
            if (currentView !== 'Year') {
                setLastYearFetched(null);
            }
        }
    }, [dispatch, selectedCompanyId, currentDate.getMonth(), currentDate.getFullYear(), currentView]);
    const calendarItems = useMemo(() => {
        if (!MonthlyPartnerEvent?.days) {
            return [];
        }
        const items = [];
        const isYearlyRequest = MonthlyPartnerEvent.month === 0;
        MonthlyPartnerEvent.days.forEach((day) => {
            // Create a date for this specific day
            let dayDate;
            if (isYearlyRequest) {
                dayDate = new Date(MonthlyPartnerEvent.year, 0, day.day);
            }
            else {
                dayDate = new Date(MonthlyPartnerEvent.year, MonthlyPartnerEvent.month - 1, day.day);
            }
            day.events.forEach((event) => {
                items.push({
                    id: `${event.eventId}-${day.day}`,
                    title: event.title,
                    start: dayDate,
                    end: dayDate,
                    startTime: event.startTime,
                    type: 'event',
                    status: event.status === 'approved' ? 'approved' : 'pending',
                    eventId: event.eventId
                });
            });
        });
        return items;
    }, [MonthlyPartnerEvent]);
    const rows = useMemo(() => (partners?.items ?? []).map((p) => ({
        id: p.id,
        partnerName: p.fullName?.trim() || p.name?.trim() || `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
        avatar: p.logoUrl ?? null,
        description: p.description,
        companyNames: p.companyNames ?? [],
        interestTitles: p.interestTitles ?? [],
        events: p.eventsCount ?? 0,
        upcomingEventName: p.upcomingEventTitle || '',
        upcomingEventDate: (() => {
            const formatPrettyDate = (dateString) => {
                if (!dateString)
                    return '';
                const d = new Date(dateString);
                if (isNaN(d.getTime()))
                    return '';
                return d.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                });
            };
            return (formatPrettyDate(p.upcomingEventStartDate) ||
                formatPrettyDate(p.upcomingEventEndDate) ||
                '');
        })(),
        benefitsUtilization: 0,
        status: p.status === PartnerStatusEnum.Active ? 'Active' : 'Inactive'
    })), [partners]);
    const handleRowClick = (params) => {
        navigate(`/sponsorship-details/${params.row.id}`);
    };
    const handleButtonClick = (params) => {
        if (!canCreatePartners) {
            toast.error(t('common.unauthorized') || 'You do not have permission to create partners');
            return;
        }
        navigate(`/sponsorships/new-sponsorship`);
    };
    const handleMenuOpen = (event, rowId) => {
        setMenuAnchor(event.currentTarget);
        setSelectedRowId(String(rowId));
    };
    // Keep selectedRowId so the menu labels (Activate/Deactivate) don't flip during the close animation;
    // it is overwritten on the next handleMenuOpen.
    const handleMenuClose = () => {
        setMenuAnchor(null);
    };
    const handleEdit = (id) => {
        if (!id) {
            handleMenuClose();
            return;
        }
        // Navigate to existing add sponsorship UI in edit mode
        navigate(`/sponsorships/new-sponsorship?edit=1&id=${id}`);
        handleMenuClose();
    };
    const handleDelete = async (id) => {
        if (!id) {
            handleMenuClose();
            return;
        }
        try {
            await dispatch(deletePartnerAsync(id)).unwrap();
            const companyIds = companies && companies.length > 0
                ? companies.map((c) => c.id)
                : selectedCompanyId
                    ? selectedCompanyId
                    : [];
            if (companyIds.length > 0) {
                await dispatch(getAllPartnersAsync({
                    companyIds,
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize
                }));
            }
        }
        catch {
            // Toast handled in thunk
        }
        finally {
            handleMenuClose();
        }
    };
    const handleToggleStatus = async (id) => {
        if (!id) {
            handleMenuClose();
            return;
        }
        try {
            const wasActive = selectedRow?.status === 'Active';
            await dispatch(togglePartnerContractAsync({ id, wasActive: !!wasActive })).unwrap();
            const companyIds = companies && companies.length > 0
                ? companies.map((c) => c.id)
                : selectedCompanyId
                    ? selectedCompanyId
                    : [];
            if (companyIds.length > 0) {
                await dispatch(getAllPartnersAsync({
                    companyIds,
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize
                }));
            }
        }
        catch {
            // Toast handled in thunk
        }
        finally {
            handleMenuClose();
        }
    };
    const tabLabels = [
        { label: t('sponsorships.vdkGroep'), value: 0, disabled: false },
        { label: t('sponsorships.allCompanies'), value: 1, disabled: false }
    ];
    const subTabLabels = [
        { label: t('common.partners'), value: 0, icon: _jsx(Category, {}) },
        { label: t('sponsorships.calendar'), value: 1, icon: _jsx(Calendar, {}) }
    ];
    const sponsorshipColumns = [
        {
            field: 'partner',
            headerName: t('sponsorships.partner'),
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }, children: [_jsx(Avatar, { name: params.row.partnerName?.charAt(0) || '?', size: 40, src: params.row.avatar }), _jsx(Typography, { variant: 'body2', fontWeight: 400, textOverflow: 'ellipsis', overflow: 'hidden', noWrap: true, children: params.row.partnerName })] }))
        },
        {
            field: 'interests',
            headerName: t('labels.tags'),
            flex: 0.6,
            minWidth: 140,
            renderCell: (params) => {
                const interestTitles = params.row.interestTitles || [];
                if (!interestTitles || interestTitles.length === 0) {
                    return (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: "-" }));
                }
                if (interestTitles.length === 1) {
                    return (_jsx(Typography, { variant: 'body2', color: 'text.primary', textOverflow: 'ellipsis', overflow: 'hidden', noWrap: true, title: interestTitles[0]?.replace(/,\s*$/, ''), children: interestTitles[0]?.replace(/,\s*$/, '') }));
                }
                return (_jsx(Tooltip, { title: interestTitles.map((t) => t.replace(/,\s*$/, '')).join(', '), children: _jsx(Typography, { variant: 'body2', color: 'text.primary', sx: { cursor: 'default' }, children: interestTitles.length }) }));
            }
        },
        {
            field: 'description',
            headerName: t('sponsorships.description'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsx(Tooltip, { title: params.value || '', arrow: true, placement: 'top', children: _jsx(Typography, { variant: 'body2', color: 'text.primary', textOverflow: 'ellipsis', overflow: 'hidden', noWrap: true, sx: { cursor: 'default' }, children: params.value }) }))
        },
        {
            field: 'company',
            headerName: t('sponsorships.company'),
            flex: 0.6,
            minWidth: 140,
            renderCell: (params) => {
                const companyNames = params.row.companyNames || [];
                if (!companyNames || companyNames.length === 0) {
                    return (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: "-" }));
                }
                if (companyNames.length === 1) {
                    return (_jsx(Typography, { variant: 'body2', color: 'text.primary', textOverflow: 'ellipsis', overflow: 'hidden', noWrap: true, title: companyNames[0], children: companyNames[0] }));
                }
                return (_jsx(Tooltip, { title: companyNames.join(', '), children: _jsx(Typography, { variant: 'body2', color: 'text.primary', sx: { cursor: 'default' }, children: companyNames.length }) }));
            }
        },
        {
            field: 'events',
            headerName: t('sponsorships.events'),
            width: 80,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', fontWeight: 400, children: params.value }))
        },
        {
            field: 'upcomingEvent',
            headerName: t('sponsorships.upcomingEvent'),
            flex: 0.5,
            minWidth: 100,
            type: 'string',
            renderCell: (params) => (_jsxs(Box, { flex: 1, minWidth: 0, children: [_jsx(Typography, { variant: 'body2', fontWeight: 400, textOverflow: 'ellipsis', overflow: 'hidden', noWrap: true, children: params.row.upcomingEventName }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', children: params.row.upcomingEventDate })] }))
        },
        // {
        //   field: 'benefitsUtilization',
        //   headerName: t<string>('sponsorships.benefitsUtilization'),
        //   flex: 0.5,
        //   minWidth: 140,
        //   renderCell: (params: any) => (
        //     <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
        //       <LinearProgress
        //         variant='determinate'
        //         value={params.value}
        //         sx={{
        //           flexGrow: 1,
        //           height: 4,
        //           borderRadius: 3,
        //           backgroundColor: theme.palette.grey[200],
        //           '& .MuiLinearProgress-bar': {
        //             borderRadius: 3,
        //             backgroundColor:
        //               params.value >= 80
        //                 ? theme.palette.success.main
        //                 : params.value >= 50
        //                   ? theme.palette.warning.main
        //                   : theme.palette.error.main
        //           }
        //         }}
        //       />
        //       <Typography variant='caption' fontWeight={400} sx={{ minWidth: 'fit-content' }}>
        //         {params.value}%
        //       </Typography>
        //     </Box>
        //   )
        // },
        {
            field: 'status',
            headerName: t('sponsorships.status'),
            width: 120,
            align: 'center',
            headerAlign: 'center',
            type: 'string',
            renderCell: (params) => {
                const value = params.value;
                const status = typeof value === 'string' ? value.trim().toLowerCase() : '';
                let variant = 'contained';
                let color = 'inherit';
                if (status === 'active') {
                    color = 'primary';
                }
                else if (status === 'pending') {
                    color = 'warning';
                }
                else if (status === 'expired') {
                    color = 'error';
                }
                const statusLabel = status === 'active'
                    ? t('common.active')
                    : status === 'inactive'
                        ? t('common.inactive')
                        : status === 'pending'
                            ? t('common.pending')
                            : status === 'expired'
                                ? t('common.expired')
                                : (value ?? '');
                return (_jsx(Button, { variant: variant, color: color, size: 'medium', sx: { pointerEvents: 'none' }, children: statusLabel }));
            }
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            type: 'string',
            renderCell: (params) => {
                // Check if user has any permissions to perform actions on this row
                const hasActions = canUpdatePartners || canDeletePartners;
                return (_jsx(Box, { onClick: (e) => e.stopPropagation(), children: _jsx(IconButton, { size: 'small', sx: { color: 'text.secondary' }, disabled: !hasActions, onClick: (event) => {
                            if (hasActions) {
                                handleMenuOpen(event, params.row.id);
                            }
                        }, children: _jsx(MoreHoriz, {}) }) }));
            }
        }
    ];
    const selectedRow = selectedRowId != null ? (rows || []).find((r) => String(r.id) === String(selectedRowId)) : null;
    const toggleText = selectedRow?.status === 'Active'
        ? (t('actions.deactivate') ?? 'Deactivate')
        : (t('actions.activate') ?? 'Activate');
    const actionOptions = [
        {
            id: 'edit',
            text: t('tasks.edit'),
            group: true,
            disabled: !canUpdatePartners,
            to: () => {
                if (canUpdatePartners) {
                    handleEdit(selectedRowId);
                }
            }
        },
        {
            id: 'toggle',
            text: toggleText,
            group: true,
            disabled: !canUpdatePartners,
            to: () => {
                if (canUpdatePartners) {
                    handleToggleStatus(selectedRowId);
                }
            }
        },
        {
            id: 'delete',
            text: t('tasks.delete'),
            group: true,
            disabled: !canDeletePartners,
            to: () => {
                if (canDeletePartners) {
                    setDeleteTargetId(selectedRowId);
                    setDeleteTargetName(selectedRow?.partnerName || '');
                    setDeleteDialogOpen(true);
                }
            }
        }
    ];
    return (_jsxs(PageLayout, { title: t('common.partners'), secondTitle: t('sponsorships.viewAndManagePartnersHere'), leftIcon: _jsx(Coin, { variant: 'Outline' }), showLeftIcon: true, rightContent: _jsx(Button, { onClick: handleButtonClick, startIcon: _jsx(AddCircle, {}), fullWidth: isSmallScreen, variant: 'contained', size: isSmallScreen ? 'medium' : 'large', color: 'primary', disabled: !canCreatePartners, children: t('sponsorships.newSponsorship') }), children: [_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5), sx: {
                    height: '100%',
                    overflow: isSmallScreen ? 'hidden' : 'visible'
                }, children: [_jsx(Box, { sx: {
                            position: isSmallScreen ? 'sticky' : 'static',
                            top: 0,
                            zIndex: 10
                        } }), _jsx(Box, { sx: {
                            flex: 1,
                            overflow: isSmallScreen ? 'auto' : 'visible'
                        }, children: _jsxs(Box, { children: [activeTab === 0 && (_jsxs(Box, { children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', children: [_jsx(Tabs, { value: activeSubTab, onChange: (_, newValue) => setActiveSubTab(newValue), sx: {
                                                        '& .MuiTab-root': {
                                                            minHeight: 48,
                                                            textTransform: 'none',
                                                            fontSize: '14px',
                                                            fontWeight: 500
                                                            // Non-active tab color
                                                        },
                                                        '& .MuiTab-root:not(.Mui-selected)': {
                                                            color: theme.palette.text.primary
                                                        },
                                                        '& .MuiTab-root.Mui-selected': {
                                                            color: theme.palette.text.primary // Active tab color
                                                        }
                                                    }, children: subTabLabels.map((tab) => (_jsx(Tab, { label: tab.label, value: tab.value }, tab.value))) }), activeSubTab === 0 && (_jsx(ResponsiveSearch, { value: searchTerm || '', onChange: setSearchTerm }))] }), _jsxs(Box, { pt: theme.spacing(1.5), children: [activeSubTab === 0 && (_jsx(Box, { children: _jsx(PrimaryGrid, { rows: rows, haveSearch: false, showFilters: false, onRowClick: handleRowClick, columns: sponsorshipColumns, maxHeight: 'calc(100vh - 300px)', paginationMode: 'server', paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, rowCount: partners?.totalCount || 0, loading: status === APIStatus.PENDING, noRowsText: t('dataGrid.noRowsAdd', {
                                                            entityPlural: t('common.partners').toLowerCase?.(),
                                                            entitySingular: t('common.partner').toLowerCase?.()
                                                        }), disableRowSelectionOnClick: false, showHeaderControls: false }) })), activeSubTab === 1 && (_jsx(Box, { children: _jsx(ScheduleCalendar, { items: calendarItems, onDateChange: setCurrentDate, onViewChange: setCurrentView }) }))] })] })), activeTab === 1 && (_jsx(Box, { children: _jsx("div", { children: "All Companies content here" }) }))] }) })] }), _jsx(StyledMenu, { anchorEl: menuAnchor, open: Boolean(menuAnchor), onClose: handleMenuClose, actions: actionOptions }), _jsx(ConfirmationDialog, { open: deleteDialogOpen, onClose: () => {
                    setDeleteDialogOpen(false);
                    setDeleteTargetId(null);
                    setDeleteTargetName('');
                }, title: t('common.deleteConfirmation'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { size: 'large', fullWidth: true, variant: 'outlined', onClick: () => {
                                setDeleteDialogOpen(false);
                                setDeleteTargetId(null);
                                setDeleteTargetName('');
                            }, children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'error', fullWidth: true, onClick: async () => {
                                await handleDelete(deleteTargetId);
                                setDeleteDialogOpen(false);
                                setDeleteTargetId(null);
                                setDeleteTargetName('');
                            }, children: t('actions.deleteNow') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', color: 'text.primary', children: t('sponsorships.deletePartnerConfirmation', {
                            name: deleteTargetName,
                            defaultValue: 'Are you sure you want to delete "{{name}}"?'
                        }) }) }) })] }));
};
export default Sponsorships;
